import axios from "axios";
import { getApiError, getStatusCodeFromError } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse } from "@entrilia/shared/api/types";
import type {
  ArtifactSourceError,
  ArtifactSourceReady,
  ArtifactSourceRef,
  ArtifactSourceState,
} from "@entrilia/shared/artifactRuntime/protocol";
import { logError } from "@entrilia/shared/logging";
import type { StateAction } from "@entrilia/shared/types";
import biApi from "../../../../../api/biApi";
import type { SmartViewArtifactDrillDown } from "../../../../../api/types/smartViewArtifactsTypes";
import { toFilterValueList } from "../report/sourceReportRequest";
import type {
  SourceFigureTarget,
  SourceNote,
  SourceRequestsState,
  SourceRun,
  SourceTarget,
} from "./sourceRequestsState";
import {
  changeDrillModeAction,
  changeFilterValuesAction,
  createSourceRequestsState,
  getFigureTarget,
  getRunsToAbort,
  getRunsToStart,
  getSourceStateUpdates,
  getUnknownSourceError,
  listActiveTargets,
  listSourceNotes,
  refreshAction,
  removeClientAction,
  settleRunAction,
  subscribeAction,
  unsubscribeAction,
} from "./sourceRequestsState";
import { isExpectedRunFailure } from "./runErrorMessage";

export interface ArtifactSourceConnection {
  artifactId: string;
  version: number;
  sources: ArtifactSourceRef[];
  filterValues: Record<string, string[]>;
  onSourceState: (subscriptionId: string, state: ArtifactSourceState) => void;
}

export interface ArtifactSourceClient {
  subscribe: (subscriptionId: string, sourceKey: string, overrides: Record<string, string[]>) => void;
  unsubscribe: (subscriptionId: string) => void;
  reset: () => void;
  changeFilterValues: (filterValues: Record<string, string[]>) => void;
  refresh: () => void;
  setDrillMode: (enabled: boolean) => void;
  getFigureTarget: (
    subscriptionId: string,
    row: number | "totals" | undefined,
    columnKey: string
  ) => SourceFigureTarget | undefined;
  listActiveTargets: () => SourceTarget[];
  listSourceNotes: () => SourceNote[];
  close: () => void;
}

export interface ArtifactSourceStore {
  connect: (connection: ArtifactSourceConnection) => ArtifactSourceClient;
}

interface SharedVersion {
  artifactId: string;
  state: SourceRequestsState;
  controllers: Map<number, AbortController>;
  listeners: Map<string, ArtifactSourceConnection["onSourceState"]>;
  isDisposed: boolean;
}

interface LoadedSource {
  state: ArtifactSourceReady | ArtifactSourceError;
  drillDown?: SmartViewArtifactDrillDown;
}

const RUN_FAILED_MESSAGE = "The data for this part of the dashboard could not be loaded.";

const hasServerMessage = (error: unknown): boolean =>
  axios.isAxiosError<ApiResponse<unknown> | undefined>(error) && Boolean(error.response?.data?.error?.message);

const loadSource = async (
  artifactId: string,
  version: number,
  run: SourceRun,
  signal: AbortSignal
): Promise<LoadedSource | undefined> => {
  try {
    const request = {
      filterValuesUserInput: toFilterValueList(run.filterValues),
      overridesUserInput: toFilterValueList(run.overrides),
      withDrillDown: run.drillDown,
    };
    const response = await biApi.smartViewArtifacts.runSource(artifactId, version, run.sourceKey, request, signal);
    if (!response.success) {
      logError(response.error, "[ArtifactSourceStore] runSource");
      return { state: { status: "error", message: response.error?.message || RUN_FAILED_MESSAGE } };
    }
    const { columns, rows, rowCount, totals, drillDown } = response.data;
    const drillCells = drillDown && {
      rows: drillDown.rows.map((rowIds) => Object.keys(rowIds)),
      totals: Object.keys(drillDown.totals),
    };
    return {
      state: { status: "ready", columns, rows, rowCount, totals, ...(drillCells && { drillCells }) },
      drillDown,
    };
  } catch (error) {
    const apiError = getApiError(error);
    if (apiError.isCanceledRequest) {
      return undefined;
    }
    if (!isExpectedRunFailure(getStatusCodeFromError(error), apiError.message)) {
      logError(error, "[ArtifactSourceStore] runSource");
    }
    return { state: { status: "error", message: hasServerMessage(error) ? apiError.message : RUN_FAILED_MESSAGE } };
  }
};

const abortRuns = (shared: SharedVersion): void => {
  shared.controllers.forEach((controller) => controller.abort());
  shared.controllers.clear();
};

const abortRun = (shared: SharedVersion, run: SourceRun): void => {
  shared.controllers.get(run.runId)?.abort();
  shared.controllers.delete(run.runId);
};

const dispatch = (shared: SharedVersion, action: StateAction<SourceRequestsState>): void => {
  if (shared.isDisposed) {
    return;
  }
  const previous = shared.state;
  shared.state = action(previous);
  for (const { clientId, subscriptionId, state } of getSourceStateUpdates(previous, shared.state)) {
    shared.listeners.get(clientId)?.(subscriptionId, state);
  }
  getRunsToAbort(previous, shared.state).forEach((run) => abortRun(shared, run));
  getRunsToStart(previous, shared.state).forEach((run) => startRun(shared, run));
};

const startRun = async (shared: SharedVersion, run: SourceRun): Promise<void> => {
  const controller = new AbortController();
  shared.controllers.set(run.runId, controller);
  const result = await loadSource(shared.artifactId, shared.state.version, run, controller.signal);
  // A run switched off and on again restarts under the same run id with a new controller.
  if (shared.controllers.get(run.runId) === controller) {
    shared.controllers.delete(run.runId);
  }
  if (result) {
    dispatch(shared, settleRunAction(run, result.state, result.drillDown));
  }
};

export const createArtifactSourceStore = (): ArtifactSourceStore => {
  const sharedVersions = new Map<string, SharedVersion>();
  let lastClientId = 0;

  const connect = ({
    artifactId,
    version,
    sources,
    filterValues,
    onSourceState,
  }: ArtifactSourceConnection): ArtifactSourceClient => {
    const sharedKey = JSON.stringify([artifactId, version]);
    const shared = sharedVersions.get(sharedKey) ?? {
      artifactId,
      state: createSourceRequestsState(version, sources, filterValues),
      controllers: new Map<number, AbortController>(),
      listeners: new Map(),
      isDisposed: false,
    };
    sharedVersions.set(sharedKey, shared);
    lastClientId += 1;
    const clientId = String(lastClientId);
    shared.listeners.set(clientId, onSourceState);
    let isClosed = false;

    const dispatchOpen = (action: StateAction<SourceRequestsState>): void => {
      if (!isClosed) {
        dispatch(shared, action);
      }
    };

    return {
      subscribe: (subscriptionId, sourceKey, overrides) => {
        const unknownSourceError = getUnknownSourceError(shared.state, sourceKey);
        if (!unknownSourceError) {
          dispatchOpen(subscribeAction(clientId, subscriptionId, sourceKey, overrides));
        } else if (!isClosed) {
          onSourceState(subscriptionId, unknownSourceError);
        }
      },
      unsubscribe: (subscriptionId) => dispatchOpen(unsubscribeAction(clientId, subscriptionId)),
      reset: () => dispatchOpen(removeClientAction(clientId)),
      changeFilterValues: (values) => dispatchOpen(changeFilterValuesAction(values)),
      refresh: () => {
        if (!isClosed) {
          abortRuns(shared);
          dispatch(shared, refreshAction());
        }
      },
      setDrillMode: (enabled) => dispatchOpen(changeDrillModeAction(clientId, enabled)),
      getFigureTarget: (subscriptionId, row, columnKey) =>
        isClosed ? undefined : getFigureTarget(shared.state, clientId, subscriptionId, row, columnKey),
      listActiveTargets: () => (isClosed ? [] : listActiveTargets(shared.state, clientId)),
      listSourceNotes: () => (isClosed ? [] : listSourceNotes(shared.state, clientId)),
      close: () => {
        if (isClosed) {
          return;
        }
        dispatch(shared, removeClientAction(clientId));
        dispatch(shared, changeDrillModeAction(clientId, false));
        isClosed = true;
        shared.listeners.delete(clientId);
        if (shared.listeners.size === 0) {
          shared.isDisposed = true;
          abortRuns(shared);
          sharedVersions.delete(sharedKey);
        }
      },
    };
  };

  return { connect };
};
